/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {makeStyles, Tooltip} from '@material-ui/core';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import ApplicationFeatures from '@ion/core/src/security/ApplicationFeatures';
import {GroupChooserIcon} from '@ion/dls/src/icons/current/GroupChooserIcon';
import {TrackingFilterIcon} from '@ion/dls/src/icons/current/TrackingFilterIcon';
import {SideNavTrackingIcon} from '@ion/dls/src/icons/SideNavTrackingIcon';
import TopNavSearchIcon from '@ion/dls/src/icons/TopNavSearchIcon';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsShadow} from '@ion/dls/src/theme/theme';
import {Color, IconSize} from '@ion/dls/src/types/types';
import {
  APP_GROUP_CHOOSER,
  TRACKING_PAGE,
  TRACKING_PAGE2
} from '@/security/Features';

/**
 * Defines the tooltip override classes consumed by the current navigation header utility tooltips.
 */
interface TooltipOverrideClasses {
  popper: string;
  tooltip: string;
}

/**
 * Defines the props for current navigation header utility content.
 */
interface NavigationHeaderUtilitiesProps {
  Features?: ApplicationFeatures;
  collapsed: boolean;
  globalGroupTitle?: string;
  isViewingGlobalGroup?: boolean;
  mobile?: boolean;
  onOpenGroupChooser: () => void;
  onOpenTracking: () => void;
  onOpenTrackingFilter: () => void;
  onOpenTrackingSearch: () => void;
  tooltipClasses: TooltipOverrideClasses;
  trackingFilterBadgeCount?: number;
  trackingFilterSelected?: boolean;
  trackingSelected?: boolean;
}

const useStyles = makeStyles(() => ({
  collapsedUtilityColumn : {
    alignItems    : 'center',
    display       : 'flex',
    flexDirection : 'column',
    gap           : 8,
    width         : 48
  },
  // Figma's frame sizes include the border (drawn inside), so each padding
  // here is 1px under Figma's to keep its 40x132 / 224x104 outer size ...
  collapsedUtilityCard : {
    alignItems    : 'center',
    background    : dlsColor('grey6'),
    border        : `1px solid ${dlsColor('grey5')}`,
    borderRadius  : 12,
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    gap           : 12,
    padding       : '7px 0',
    width         : 40
  },
  // 32px tile with the 24px search icon centred in it, per the design ...
  collapsedSearchTile : {
    alignItems        : 'center',
    background        : dlsColor('white'),
    border            : `1px solid ${dlsColor('grey5')}`,
    borderRadius      : 8,
    boxSizing         : 'border-box',
    display           : 'flex',
    height            : 32,
    justifyContent    : 'center',
    cursor            : 'pointer',
    width             : 32,
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('darkTeal')}`,
      outlineOffset : 2
    }
  },
  collapsedUtilityStack : {
    alignItems    : 'center',
    display       : 'flex',
    flexDirection : 'column',
    height        : 72,
    width         : 32
  },
  collapsedTrackingTile : {
    alignItems     : 'center',
    background     : dlsColor('navBarGrey'),
    border         : `1px solid ${dlsColor('pastelTeal')}`,
    borderBottom   : 'none',
    borderRadius   : '8px 8px 0 0',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 36,
    justifyContent : 'center',
    cursor         : 'pointer',
    position       : 'relative',
    width          : 32
  },
  // the Filter tile's top border is the line between the two tiles ...
  collapsedTrackingTileSelected : {
    background   : dlsColor('paleBlue'),
    border       : `1px solid ${dlsColor('pastelTeal')}`,
    borderBottom : 'none'
  },
  collapsedFilterTile : {
    alignItems     : 'center',
    background     : dlsColor('navBarGrey'),
    border         : `1px solid ${dlsColor('pastelTeal')}`,
    borderRadius   : '0 0 8px 8px',
    boxSizing      : 'border-box',
    display        : 'flex',
    height         : 36,
    justifyContent : 'center',
    cursor         : 'pointer',
    position       : 'relative',
    width          : 32
  },
  collapsedFilterTileSelected : {
    background : dlsColor('paleBlue'),
    border     : `1px solid ${dlsColor('pastelTeal')}`
  },
  utilityButtonFocus : {
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('darkTeal')}`,
      outlineOffset : 2
    }
  },
  filterBadgeCollapsed : {
    alignItems      : 'center',
    backgroundColor : dlsColor('darkestBlue'),
    borderRadius    : '50%',
    bottom          : -8,
    boxSizing       : 'border-box',
    color           : dlsColor('white'),
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 10,
    fontWeight      : 600,
    height          : 20,
    justifyContent  : 'center',
    lineHeight      : '12px',
    position        : 'absolute',
    right           : -8,
    width           : 20,
    zIndex          : 1
  },
  filterBadgeExpanded : {
    alignItems      : 'center',
    backgroundColor : dlsColor('darkestBlue'),
    borderRadius    : '50%',
    color           : dlsColor('white'),
    display         : 'flex',
    flexShrink      : 0,
    fontFamily      : 'Inter',
    fontSize        : 10,
    fontWeight      : 600,
    height          : 20,
    justifyContent  : 'center',
    lineHeight      : '12px',
    pointerEvents   : 'none',
    position        : 'absolute',
    right           : 2,
    top             : 6,
    width           : 20,
    zIndex          : 1
  },
  collapsedSearchIcon : {
    display : 'flex'
  },
  searchIconLarge : {
    height : 24,
    width  : 24
  },
  collapsedSearchTooltip : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxShadow       : dlsShadow(3.2),
    boxSizing       : 'border-box',
    color           : dlsColor('grey1'),
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    height          : 32,
    lineHeight      : '17px',
    padding         : '4px 16px',
    whiteSpace      : 'nowrap'
  },
  collapsedGroupTooltip : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxShadow       : dlsShadow(3.2),
    boxSizing       : 'border-box',
    display         : 'flex',
    gap             : 8,
    height          : 32,
    maxHeight       : 32,
    minHeight       : 32,
    padding         : '4px 16px',
    width           : 166
  },
  collapsedGroupTooltipActive : {
    backgroundColor : dlsColor('paleBlue2')
  },
  collapsedGroupTooltipText : {
    alignItems    : 'flex-start',
    display       : 'flex',
    flexDirection : 'column',
    flexGrow      : 1,
    height        : 29,
    padding       : 0,
    width         : 134
  },
  collapsedGroupTooltipTitle : {
    alignSelf  : 'stretch',
    color      : '#0C6178',
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 500,
    lineHeight : '17px'
  },
  collapsedGroupTooltipSubtitle : {
    color         : dlsColor('lightTeal'),
    fontFamily    : 'Inter',
    fontSize      : 10,
    fontWeight    : 600,
    letterSpacing : '0.5px',
    lineHeight    : '12px',
    overflow      : 'hidden',
    textOverflow  : 'ellipsis',
    whiteSpace    : 'nowrap',
    width         : 134
  },
  collapsedGroupRow : {
    alignItems     : 'center',
    borderBottom   : `1.5px solid ${dlsColor('pastelTeal')}`,
    boxSizing      : 'border-box',
    display        : 'flex',
    filter         : 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.12))',
    height         : 44,
    justifyContent : 'center',
    padding        : '6px 8px',
    width          : 48
  },
  collapsedGroupButton : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : '1px solid transparent',
    borderRadius    : 8,
    boxSizing       : 'border-box',
    color           : dlsColor('grey1'),
    cursor          : 'pointer',
    display         : 'flex',
    gap             : 8,
    height          : 32,
    justifyContent  : 'center',
    padding         : 4,
    transition      : 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    width           : 32
  },
  collapsedGroupButtonInteractive : {
    '&:hover' : {
      backgroundColor : dlsColor('white'),
      border          : `1px solid ${dlsColor('grey5')}`,
      boxShadow       : '0 1px 2px rgba(0, 0, 0, 0.12)',
      color           : dlsColor('darkTeal')
    },
    '&:active' : {
      backgroundColor : dlsColor('paleBlue2'),
      border          : '1px solid transparent',
      boxShadow       : 'none',
      color           : dlsColor('darkTeal')
    }
  },
  collapsedGroupButtonSelected : {
    backgroundColor : dlsColor('paleBlue'),
    border          : '1px solid transparent',
    boxShadow       : 'none',
    color           : dlsColor('darkTeal')
  },
  expandedUtilitySection : {
    borderBottom  : `1.5px solid ${dlsColor('pastelTeal')}`,
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    gap           : 4,
    // Figma's 4px bottom padding less its 1.5px border, which Figma counts
    // inside the section's 156px ...
    padding       : '0 0 2.5px',
    width         : '100%'
  },
  expandedUtilityCard : {
    alignItems    : 'flex-start',
    background    : dlsColor('grey6'),
    border        : `1px solid ${dlsColor('grey5')}`,
    borderRadius  : 12,
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    gap           : 8,
    padding       : '7px 7px 1px',
    width         : '100%'
  },
  // the phone sidesheet keeps its earlier spacing - the condensed sizes are
  // the desktop nav's ...
  mobileUtilitySection : {
    '&&' : {
      gap     : 8,
      padding : '0 0 8px'
    }
  },
  mobileUtilityCard : {
    '&&' : {
      padding : '16px 8px 8px'
    }
  },
  mobileUtilityPlaceholder : {
    '&&' : {
      gap : 8
    }
  },
  mobileTrackingFilterRow : {
    '&&' : {
      height : 48
    }
  },
  mobileGroupRow : {
    '&&' : {
      height  : 48,
      margin  : '0 -4px',
      padding : 8,
      width   : 'calc(100% + 8px)'
    }
  },
  expandedUtilityPlaceholder : {
    display       : 'flex',
    flexDirection : 'column',
    gap           : 6,
    width         : '100%'
  },
  expandedSearchRow : {
    display : 'flex',
    height  : 44,
    width   : '100%'
  },
  expandedSearchBox : {
    alignItems        : 'center',
    background        : dlsColor('white'),
    border            : `1px solid ${dlsColor('grey5')}`,
    borderRadius      : 8,
    boxSizing         : 'border-box',
    cursor            : 'pointer',
    display           : 'flex',
    gap               : 8,
    height            : 44,
    padding           : 16,
    width             : '100%',
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('darkTeal')}`,
      outlineOffset : 2
    }
  },
  expandedSearchText : {
    color      : dlsColor('grey3'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 400,
    lineHeight : '20px'
  },
  // Figma: a 44px row with the 32px Tracking / Filter control centred in it ...
  expandedTrackingFilterRow : {
    alignItems : 'center',
    display    : 'flex',
    height     : 44,
    width      : '100%'
  },
  expandedTrackingFilterShell : {
    alignItems   : 'center',
    border       : `1px solid ${dlsColor('pastelTeal')}`,
    borderRadius : 8,
    boxSizing    : 'border-box',
    display      : 'flex',
    height       : 32,
    position     : 'relative',
    width        : '100%'
  },
  expandedTrackingButton : {
    alignItems   : 'center',
    background   : dlsColor('navBarGrey'),
    border       : `1px solid ${dlsColor('pastelTeal')}`,
    borderRadius : '8px 0 0 8px',
    boxSizing    : 'border-box',
    cursor       : 'pointer',
    display      : 'flex',
    gap          : 8,
    height       : 32,
    margin       : -1,
    padding      : '4px 10px 4px 8px',
    width        : 108
  },
  expandedTrackingButtonSelected : {
    background : dlsColor('paleBlue'),
    border     : `1px solid ${dlsColor('pastelTeal')}`
  },
  expandedFilterButton : {
    alignItems     : 'center',
    background     : dlsColor('navBarGrey'),
    border         : `1px solid ${dlsColor('pastelTeal')}`,
    borderRadius   : '0 8px 8px 0',
    boxSizing      : 'border-box',
    cursor         : 'pointer',
    display        : 'flex',
    flex           : 1,
    height         : 32,
    justifyContent : 'flex-start',
    margin         : -1,
    // Figma specs 4px 8px for this button's own padding - the extra right
    // padding only belongs here when the absolutely-positioned filter
    // count badge is actually rendered (see expandedFilterButtonWithBadge
    // below), not unconditionally ...
    padding        : '4px 8px'
  },
  // applied only when hasActiveTrackingFilters is true, so the "Filter"
  // label has room to its right for the badge (which is positioned
  // absolute and so doesn't reserve its own space in normal flow) ...
  expandedFilterButtonWithBadge : {
    paddingRight : 28
  },
  expandedFilterButtonSelected : {
    background : dlsColor('paleBlue'),
    border     : `1px solid ${dlsColor('pastelTeal')}`
  },
  utilityLabel : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 500,
    // Figma specs this text's line-height equal to its own 20px box height,
    // not 17px, for both the Tracking and Filter labels that share this ...
    lineHeight : '20px'
  },
  utilityLabelSelected : {
    color : dlsColor('darkTeal')
  },
  expandedGroupRow : {
    alignItems        : 'center',
    boxSizing         : 'border-box',
    cursor            : 'pointer',
    display           : 'flex',
    gap               : 8,
    height            : 44,
    padding           : '6px 8px',
    width             : '100%',
    '&:focus-visible' : {
      outline       : `2px solid ${dlsColor('darkTeal')}`,
      outlineOffset : -2
    }
  },
  expandedGroupRowInteractive : {
    '&:hover $expandedGroupContent' : {
      backgroundColor : dlsColor('white'),
      border          : `1px solid ${dlsColor('grey5')}`,
      boxShadow       : '0 1px 2px rgba(0, 0, 0, 0.12)',
      color           : dlsColor('lightTeal')
    },
    '&:hover $utilityLabel' : {
      color : dlsColor('lightTeal')
    },
    '&:active $expandedGroupContent' : {
      backgroundColor : dlsColor('paleBlue2'),
      border          : '1px solid transparent',
      boxShadow       : 'none',
      color           : dlsColor('darkTeal')
    },
    '&:active $utilityLabel' : {
      color : dlsColor('darkTeal')
    }
  },
  expandedGroupContent : {
    alignItems   : 'center',
    border       : '1px solid transparent',
    borderRadius : 8,
    boxSizing    : 'border-box',
    color        : dlsColor('grey1'),
    display      : 'flex',
    gap          : 8,
    height       : 32,
    padding      : '4px 8px',
    transition   : 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    width        : '100%'
  },
  expandedGroupContentSelected : {
    backgroundColor : dlsColor('paleBlue'),
    border          : '1px solid transparent',
    boxShadow       : 'none',
    color           : dlsColor('darkTeal')
  },
  expandedGroupText : {
    display        : 'flex',
    flex           : 1,
    flexDirection  : 'column',
    justifyContent : 'center',
    minWidth       : 0
  },
  expandedGroupSubtitle : {
    color         : dlsColor('lightTeal'),
    fontFamily    : 'Inter',
    fontSize      : 10,
    fontWeight    : 600,
    letterSpacing : '0.5px',
    lineHeight    : '12px',
    overflow      : 'hidden',
    textOverflow  : 'ellipsis',
    whiteSpace    : 'nowrap'
  }
}));

/**
 * Renders the current navigation utility section for collapsed and expanded states.
 *
 * @param props Defines the header utility content state and callbacks.
 * @returns The current navigation utility section.
 */
const NavigationHeaderUtilities = ({
  Features,
  collapsed,
  globalGroupTitle,
  onOpenGroupChooser,
  onOpenTracking,
  onOpenTrackingFilter,
  onOpenTrackingSearch,
  tooltipClasses,
  isViewingGlobalGroup = false,
  mobile = false,
  trackingFilterBadgeCount = 0,
  trackingFilterSelected = false,
  trackingSelected = false
}: NavigationHeaderUtilitiesProps) => {
  const classes = useStyles();

  const currentGroupLabel = globalGroupTitle || translate('nav:navigation.currentGroup');

  const hasActiveTrackingFilters = trackingFilterBadgeCount > 0;
  const trackingFilterBadgeLabel = trackingFilterBadgeCount > 99 ? '99+' : trackingFilterBadgeCount;
  const trackingColor            = trackingSelected ? dlsColor('darkTeal') : dlsColor('grey1');
  const filterColor              = trackingFilterSelected ? dlsColor('darkTeal') : dlsColor('grey1');

  const handleSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenTrackingSearch();
    }
  }, [onOpenTrackingSearch]);

  const handleTrackingKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenTracking();
    }
  }, [onOpenTracking]);

  const handleFilterKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenTrackingFilter();
    }
  }, [onOpenTrackingFilter]);

  const handleGroupChooserKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenGroupChooser();
    }
  }, [onOpenGroupChooser]);

  return (
    <Render>
      <Render.If condition={collapsed}>
        <div className={classes.collapsedUtilityColumn}>
          <Render.If condition={Features?.hasFeature(TRACKING_PAGE) || Features?.hasFeature(TRACKING_PAGE2)}>
            <div className={classes.collapsedUtilityCard}>
              <Tooltip title={(
                <span className={classes.collapsedSearchTooltip}>
                  {translate('common:search.searchTracking')}
                </span>
              )}
              placement='right'
              enterDelay={0}
              leaveDelay={0}
              classes={tooltipClasses}
              PopperProps={{
                modifiers : {
                  offset : {
                    enabled : true,
                    offset  : '8px, 0'
                  }
                }
              }}>
                <div aria-label={translate('common:search.searchTracking')}
                  onClick={onOpenTrackingSearch}
                  onKeyDown={handleSearchKeyDown}
                  role='button'
                  tabIndex={0}
                  className={classes.collapsedSearchTile}>
                  <span className={classes.collapsedSearchIcon}>
                    <TopNavSearchIcon size={IconSize.medium} color={Color.grey1} />
                  </span>
                </div>
              </Tooltip>
              <div className={classes.collapsedUtilityStack}>
                <div aria-label={translate('nav:side.tracking')}
                  onClick={onOpenTracking}
                  onKeyDown={handleTrackingKeyDown}
                  role='button'
                  tabIndex={0}
                  aria-pressed={trackingSelected}
                  className={classnames(classes.collapsedTrackingTile, classes.utilityButtonFocus, {
                    [classes.collapsedTrackingTileSelected] : trackingSelected
                  })}>
                  <SideNavTrackingIcon color={trackingColor} />
                </div>
                <div aria-label={translate('nav:navigation.filter')}
                  onClick={onOpenTrackingFilter}
                  onKeyDown={handleFilterKeyDown}
                  role='button'
                  tabIndex={0}
                  aria-pressed={trackingFilterSelected}
                  className={classnames(classes.collapsedFilterTile, classes.utilityButtonFocus, {
                    [classes.collapsedFilterTileSelected] : trackingFilterSelected
                  })}>
                  <TrackingFilterIcon color={filterColor} />
                  <Render.If condition={hasActiveTrackingFilters}>
                    <span className={classes.filterBadgeCollapsed}>
                      {trackingFilterBadgeLabel}
                    </span>
                  </Render.If>
                </div>
              </div>
            </div>
          </Render.If>
          <Render.If condition={Features?.hasFeature(APP_GROUP_CHOOSER)}>
            <Tooltip title={(
              <div className={classnames(classes.collapsedGroupTooltip, {
                [classes.collapsedGroupTooltipActive] : isViewingGlobalGroup
              })}>
                <div className={classes.collapsedGroupTooltipText}>
                  <span className={classes.collapsedGroupTooltipTitle}>{translate('nav:navigation.groupChooser')}</span>
                  <span className={classes.collapsedGroupTooltipSubtitle}>
                    {currentGroupLabel}
                  </span>
                </div>
              </div>
            )}
            placement='right'
            enterDelay={0}
            leaveDelay={0}
            classes={tooltipClasses}
            PopperProps={{
              modifiers : {
                offset : {
                  enabled : true,
                  offset  : '8px, 0'
                }
              }
            }}>
              <div className={classes.collapsedGroupRow}>
                <div aria-label={translate('nav:navigation.groupChooser')}
                  onClick={onOpenGroupChooser}
                  onKeyDown={handleGroupChooserKeyDown}
                  role='button'
                  tabIndex={0}
                  className={classnames(classes.collapsedGroupButton, {
                    [classes.collapsedGroupButtonInteractive] : !isViewingGlobalGroup,
                    [classes.collapsedGroupButtonSelected]    : isViewingGlobalGroup
                  })}>
                  <GroupChooserIcon color='currentColor' />
                </div>
              </div>
            </Tooltip>
          </Render.If>
        </div>
      </Render.If>
      <Render.Else>
        <div className={classnames(classes.expandedUtilitySection, {[classes.mobileUtilitySection]: mobile})}>
          <Render.If condition={Features?.hasFeature(TRACKING_PAGE) || Features?.hasFeature(TRACKING_PAGE2)}>
            <div className={classnames(classes.expandedUtilityCard, {[classes.mobileUtilityCard]: mobile})}>
              <div className={classnames(classes.expandedUtilityPlaceholder, {
                [classes.mobileUtilityPlaceholder] : mobile
              })}>
                <div className={classes.expandedSearchRow}>
                  <div aria-label={translate('maps:controls.searchMap')}
                    onClick={onOpenTrackingSearch}
                    onKeyDown={handleSearchKeyDown}
                    role='button'
                    tabIndex={0}
                    className={classes.expandedSearchBox}>
                    <span className={classes.searchIconLarge}>
                      <TopNavSearchIcon size={IconSize.medium} color={Color.grey1} />
                    </span>
                    <span className={classes.expandedSearchText}>
                      {translate('common:search.searchTracking')}
                    </span>
                  </div>
                </div>
                <div className={classnames(classes.expandedTrackingFilterRow, {
                  [classes.mobileTrackingFilterRow] : mobile
                })}>
                  <div className={classes.expandedTrackingFilterShell}>
                    <div aria-label={translate('nav:side.tracking')}
                      onClick={onOpenTracking}
                      onKeyDown={handleTrackingKeyDown}
                      role='button'
                      tabIndex={0}
                      aria-pressed={trackingSelected}
                      className={classnames(classes.expandedTrackingButton, classes.utilityButtonFocus, {
                        [classes.expandedTrackingButtonSelected] : trackingSelected
                      })}>
                      <SideNavTrackingIcon color={trackingColor} />
                      <span className={classnames(classes.utilityLabel, {
                        [classes.utilityLabelSelected] : trackingSelected
                      })}>{translate('nav:side.tracking')}</span>
                    </div>
                    <div aria-label={translate('nav:navigation.filter')}
                      onClick={onOpenTrackingFilter}
                      onKeyDown={handleFilterKeyDown}
                      role='button'
                      tabIndex={0}
                      aria-pressed={trackingFilterSelected}
                      className={classnames(classes.expandedFilterButton, classes.utilityButtonFocus, {
                        [classes.expandedFilterButtonSelected]  : trackingFilterSelected,
                        [classes.expandedFilterButtonWithBadge] : hasActiveTrackingFilters
                      })}>
                      <TrackingFilterIcon color={filterColor} />
                      <span className={classnames(classes.utilityLabel, {
                        [classes.utilityLabelSelected] : trackingFilterSelected
                      })}>{translate('nav:navigation.filter')}</span>
                    </div>
                    <Render.If condition={hasActiveTrackingFilters}>
                      <span className={classes.filterBadgeExpanded}>
                        {trackingFilterBadgeLabel}
                      </span>
                    </Render.If>
                  </div>
                </div>
              </div>
            </div>
          </Render.If>
          <Render.If condition={Features?.hasFeature(APP_GROUP_CHOOSER)}>
            <div onClick={onOpenGroupChooser}
              onKeyDown={handleGroupChooserKeyDown}
              role='button'
              tabIndex={0}
              className={classnames(classes.expandedGroupRow, {
                [classes.expandedGroupRowInteractive] : !isViewingGlobalGroup,
                [classes.mobileGroupRow]              : mobile
              })}>
              <div className={classnames(classes.expandedGroupContent, {
                [classes.expandedGroupContentSelected] : isViewingGlobalGroup
              })}>
                <GroupChooserIcon color='currentColor' />
                <div className={classes.expandedGroupText}>
                  <span className={classes.utilityLabel}
                    style={{color: isViewingGlobalGroup ? dlsColor('darkTeal') : undefined}}>
                    {translate('nav:navigation.groupChooser')}
                  </span>
                  <span className={classes.expandedGroupSubtitle}>
                    {currentGroupLabel}
                  </span>
                </div>
              </div>
            </div>
          </Render.If>
        </div>
      </Render.Else>
    </Render>
  );
};

export default memo(NavigationHeaderUtilities);
